"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters"
  })
});
function getSafeRedirectUrl(url: string | null): string {
  if (!url) {
    return "/";
  }
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  return "/";
}
export default function Login() {
  const $ = _c(33);
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const {
    signIn
  } = useAuth();
  let t0;
  if ($[0] !== searchParams) {
    t0 = getSafeRedirectUrl(searchParams.get("returnUrl"));
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const returnUrl = t0;
  let t1;
  if ($[2] !== returnUrl) {
    t1 = {
      redirectTo: returnUrl,
      redirectWhen: "authenticated"
    };
    $[2] = returnUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useUser(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      resolver: zodResolver(formSchema),
      defaultValues: {
        email: "",
        password: ""
      }
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm(t2);
  let t3;
  if ($[5] !== queryClient || $[6] !== router || $[7] !== signIn) {
    t3 = async function onSubmit(values) {
      setIsLoading(true);
      const {
        error
      } = await signIn.email({
        email: values.email,
        password: values.password
      }, {
        onSuccess: () => {
          queryClient.clear();
          toast.success("Login successful");
          router.push("/");
        },
        onError: _temp
      });
      if (error) {
        toast.error(error.message || "An unknown error occurred", {
          style: {
            backgroundColor: "var(--destructive)",
            color: "var(--destructive-foreground)"
          }
        });
      }
      setIsLoading(false);
    };
    $[5] = queryClient;
    $[6] = router;
    $[7] = signIn;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onSubmit = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col space-y-2 text-center"><h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1><p className="text-sm text-muted-foreground">Enter your email and password to sign in to your account</p></div>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== form || $[11] !== onSubmit) {
    t5 = form.handleSubmit(onSubmit);
    $[10] = form;
    $[11] = onSubmit;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  let t7;
  if ($[13] !== form.control) {
    t6 = <FormField control={form.control} name="email" render={_temp2} />;
    t7 = <FormField control={form.control} name="password" render={_temp3} />;
    $[13] = form.control;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isLoading) {
    t8 = isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : "Sign in";
    $[16] = isLoading;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== isLoading || $[19] !== t8) {
    t9 = <Button type="submit" className="w-full" disabled={isLoading}>{t8}</Button>;
    $[18] = isLoading;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t10 = <form onSubmit={t5} className="space-y-4">{t6}{t7}{t9}</form>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== form || $[27] !== t10) {
    t11 = <Form {...form}>{t10}</Form>;
    $[26] = form;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="relative">{t12}<div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or</span></div></div>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11) {
    t14 = <div className="px-4 sm:px-0 max-w-[64rem] mx-auto flex h-screen w-screen flex-col items-center justify-center"><div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">{t4}{t11}{t13}</div></div>;
    $[31] = t11;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function _temp3(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel>Password</FormLabel><FormControl><Input placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} type="password" autoComplete="current-password webauthn" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>Email</FormLabel><FormControl><Input placeholder="name@example.com" type="email" autoComplete="username webauthn" {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp(ctx) {
  toast.error(ctx.error.message || "An unknown error occurred", {
    style: {
      backgroundColor: "var(--destructive)",
      color: "var(--destructive-foreground)"
    }
  });
}